import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PublicShell } from "@/components/PublicShell";
import { ArrowLeft, Briefcase, CalendarX, Clock, MapPin } from "@phosphor-icons/react";

const List = ({ title, items, testid }) =>
  items?.length ? (
    <div className="mt-7" data-testid={testid}>
      <h2 className="text-base md:text-lg font-bold">{title}</h2>
      <ul className="mt-3 space-y-2">
        {items.map((i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3B82F6] shrink-0" />{i}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

export default function PositionDetail() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/careers/postings/${pid}`).then(({ data: d }) => setData(d))
      .catch(() => setError("That position is not posted."));
  }, [pid]);

  const p = data?.position;

  return (
    <PublicShell testid="position-detail-page">
      <Link to="/careers" className="inline-flex items-center gap-2 text-sm font-bold text-[#3B82F6]" data-testid="back-to-careers">
        <ArrowLeft size={16} /> All openings
      </Link>
      {error && <p className="mt-8 font-semibold text-[#EF4444]" data-testid="position-error">{error}</p>}
      {!data && !error && <p className="mt-8 text-muted-foreground">Loading position…</p>}

      {p && (
        <>
          <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6]">{p.role_label}</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">{p.title}</h1>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-muted-foreground">
            {p.location && <span className="inline-flex items-center gap-1.5"><MapPin size={16} weight="duotone" />{p.location}</span>}
            <span className="inline-flex items-center gap-1.5"><Briefcase size={16} weight="duotone" />{p.employment_type}</span>
            {p.schedule && <span className="inline-flex items-center gap-1.5"><Clock size={16} weight="duotone" />{p.schedule}</span>}
            {p.deadline && <span className="inline-flex items-center gap-1.5"><CalendarX size={16} weight="duotone" />Apply by {p.deadline}</span>}
          </div>
          {p.pay_display && <p className="mt-4 text-lg font-bold" data-testid="pay-range">{p.pay_display}</p>}
          <p className="mt-2 text-xs text-muted-foreground">
            {p.openings > 1 ? `${p.openings} openings · ` : ""}Posted {p.posted_at ? new Date(p.posted_at).toLocaleDateString() : "recently"}
          </p>

          {!p.accepting ? (
            <div className="mt-8 rounded-2xl border-2 border-amber-300 bg-amber-50 p-6" data-testid="position-closed-note">
              <p className="font-bold">This position is closed to new applications</p>
              <p className="mt-2 text-sm">{p.closed_reason}</p>
              <Button asChild variant="outline" className="mt-5 rounded-full font-bold">
                <Link to="/careers/apply/interest" data-testid="closed-join-pool">Join the future-opportunities pool instead</Link>
              </Button>
            </div>
          ) : (
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold px-7">
                <Link to={`/careers/apply/${p.id}`} data-testid="apply-now-btn">Apply for this position</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full font-bold">
                <Link to="/careers" data-testid="see-other-roles">See other roles</Link>
              </Button>
            </div>
          )}

          {p.summary && <p className="mt-8 text-base leading-relaxed max-w-3xl whitespace-pre-line" data-testid="position-summary">{p.summary}</p>}
          <List title="Responsibilities" items={p.responsibilities} testid="position-responsibilities" />
          <List title="Essential duties" items={p.essential_duties} testid="position-duties" />
          <List title="Qualifications" items={p.qualifications} testid="position-qualifications" />
          <List title="Credentials we look for" items={p.credentials} testid="position-credentials" />

          <div className="mt-12 grid gap-4 md:grid-cols-2">
            <div className="bg-white rounded-2xl border border-border p-6">
              <p className="font-bold">Equal employment opportunity</p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{data.eeo_statement}</p>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6">
              <p className="font-bold">Applicant privacy notice</p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{data.privacy_notice}</p>
            </div>
          </div>
        </>
      )}
    </PublicShell>
  );
}
